<template>
	<fieldset
		class="ButtonMoveDealAccountingMonth"
		:disabled="fimenu.isDealFinalized()"
	>
		<span v-if="props.showStoreAccountingMonth"> Set Deal's Accounting Month: </span>
		<div class="button-container">
			<button
				:disabled="isDealInTheSameAccountingMonthAsStore"
				@click="() => moveDealToAccountingMonth(dealsPreviousAccountingMonth)"
			>
				{{ monthsEnumsHelper.getDisplay(dealsPreviousAccountingMonth.month()) }}
			</button>
			<button @click="() => moveDealToAccountingMonth(dealsNextAccountingMonth)">
				{{ monthsEnumsHelper.getDisplay(dealsNextAccountingMonth.month()) }}
			</button>
		</div>
	</fieldset>
</template>

<script lang="ts">
	export interface IButtonMoveDealAccountingMonthProps {
		fimenu: FIMenu;
		showStoreAccountingMonth?: boolean;
	}
</script>

<script setup lang="ts">
	//#region imports
	import { computed, onBeforeMount, Ref, ref } from 'vue';
	import AccountingHelper from '@/helpers/AccountingHelper';
	import CloseAccountingMonthHistoryItem from '@core/classes/Accounting/CloseAccountingMonthHistoryItem';
	import FIMenu from '@core/classes/FIMenu';
	import moment, { Moment } from 'moment';
	import $modal from '@core/services/modal';
	import modalInfo from '@core/modals/modalInfo.vue';
	import { Month } from '@core/classes/SharedEnums';
	import MonthEnumsHelper from '@core/_enums/MonthEnumsHelper';
	import settings from '@/settings';
	import ENUMS from '@core/classes/Enums';
	//#endregion

	//#region props
	const props = defineProps<IButtonMoveDealAccountingMonthProps>();
	//#endregion

	//#region state
	const monthsEnumsHelper = new MonthEnumsHelper();
	const accountingHelper = new AccountingHelper();
	const storeAccountingMonthHistoryList = ref<CloseAccountingMonthHistoryItem[]>([]) as Ref<CloseAccountingMonthHistoryItem[]>;
	const currentStoreAccountingMonth = moment(props.fimenu.store.storeSettings.accountingSettings.currentAccountingMonth, 'YYYY-M-D').startOf('month');
	//#endregion

	//#region computed
	// A COMPUTED VALUE THAT RETURNS TRUE IF THE DEAL IS IN THE SAME ACCOUNTING MONTH AS THE STORE
	const isDealInTheSameAccountingMonthAsStore = computed((): boolean => {
		return moment(props.fimenu.accountingInfo.accountingMonth).isSame(currentStoreAccountingMonth, 'month');
	});

	onBeforeMount(async () => {
		const historyItemsList = await accountingHelper.getAllAccountingMonthHistory(props.fimenu.store.storeCode);
		//sort historyItemsList by accountingMonth
		storeAccountingMonthHistoryList.value = historyItemsList.sort(
			(a: CloseAccountingMonthHistoryItem, b: CloseAccountingMonthHistoryItem) => a.accountingMonth.getTime() - b.accountingMonth.getTime(),
		);

		// IF WE DON'T HAVE ACCOUNTING HISTORY FOR THIS STORE YET, SET THE EARLIEST MONTH AS THE CURRENT ACCOUNTING MONTH BYT CREATING A NEW HISTORY ITEM AND SETTING ISCLOSED AS FALSE
		if (!storeAccountingMonthHistoryList.value.length) {
			const newAccountingMonthHistoryItem = new CloseAccountingMonthHistoryItem({
				accountingMonth: moment(props.fimenu.accountingInfo.accountingMonth).startOf('month').toDate(),
				isClosed: false,
				storeCode: props.fimenu.store.storeCode,
			});
			storeAccountingMonthHistoryList.value.push(newAccountingMonthHistoryItem);
		}
	});

	// A COMPUTED VALUE THAT RETURNS THE NEXT MONTH TO THE CURRENT DEAL ACCOUNTING MONTH
	const dealsNextAccountingMonth = computed((): Moment => {
		// GET THE NEXT MONTH
		return moment(props.fimenu.accountingInfo.accountingMonth).add(1, 'month');
	});

	// A COMPUTED VALUE THAT RETURNS THE PREVIOUS MONTH TO THE CURRENT DEAL ACCOUNTING MONTH
	const dealsPreviousAccountingMonth = computed((): Moment => {
		// GET THE PREVIOUS MONTH
		return moment(props.fimenu.accountingInfo.accountingMonth).subtract(1, 'month');
	});

	//#endregion

	//#region methods
	const moveDealToAccountingMonth = (accountingMonth: Moment) => {
		const vehicleWithWeight = settings.lookups.statusSettings.filter((sS: any) => sS.type == ENUMS.STATUS_TYPES.VEHICLE && sS.weight == 1).map((sS: any) => sS.name);
		if (props.fimenu.getCurrentWeightOfDeal() != 1) {
			$modal.open(modalInfo, {
				name: 'modalInfo',
				passedData: {
					title: "Deal can't be Moved",
					info: `This deal can't be moved to this accounting month (${monthsEnumsHelper.getDisplay(accountingMonth.month())}) because the vehicle status is not one of the following:
	                   \n${vehicleWithWeight.map((vW: any) => `- ${vW}`).join('\n')}`,
					acceptText: 'Close',
				},
				backdrop: false,
				postFunction: async () => {},
			});
		} else {
			$modal.open(modalInfo, {
				name: 'modalInfo',
				passedData: {
					title: 'Confirmation Required',
					info: `Are you sure you want to move this deal to this accounting month (${monthsEnumsHelper.getDisplay(accountingMonth.month())})?`,
					acceptText: 'Move Deal',
					cancelText: 'Cancel',
				},
				backdrop: false,
				postFunction: async () => {
					props.fimenu.moveDealToAccountingMonth(accountingMonth);
				},
			});
		}
	};
	//#endregion
</script>

<style scoped>
	.ButtonMoveDealAccountingMonth {
		display: flex;
		flex-direction: column;
		gap: 5px;
	}
	.ButtonMoveDealAccountingMonth span {
		text-align: center;
	}
	.ButtonMoveDealAccountingMonth .button-container {
		display: flex;
		gap: 5px;
		justify-content: center;
		align-items: center;
	}
	.ButtonMoveDealAccountingMonth .button-container button {
		width: 100%;
	}
</style>
